/*
 * SCSS → Tailwind token mapping (IntakeQuestionCard.scss deleted):
 *
 * $border-color = var(--border-card, #141E1F1A) = border-border-medium (shade[10])
 * $placeholder-color = var(--text-medium, rgba(37,56,69,0.7)) = text-text-light
 * $preview-bg = #f5f8fa ≈ bg-deep-navy-5 (#F6F7F8) — closest DS token; no exact match
 *
 * .iqc-card              -> bg-white border border-border-medium rounded-large overflow-hidden
 * .iqc-header            -> flex items-start gap-sp-1.5 px-sp-3 py-sp-2.5
 * .iqc-q-badge           -> flex-shrink-0 mt-[1px]
 * .iqc-header-content    -> flex-1 flex flex-col gap-sp-1 min-w-0
 * .iqc-question-text     -> text-text-dark break-words
 * .iqc-badges            -> flex gap-sp-1 flex-wrap
 * .iqc-divider           -> h-px bg-border-medium
 * .iqc-patient-sees      -> px-sp-3 py-sp-2 flex flex-col gap-sp-1.5
 * .iqc-patient-sees-label -> text-text-light
 * .iqc-option-list       -> flex flex-col gap-sp-1
 * .iqc-option-row        -> flex items-center gap-[10px] py-[10px] px-[14px] bg-deep-navy-5 border border-border-medium rounded-[6px]
 * .iqc-radio             -> w-4 h-4 rounded-full border-[1.5px] border-text-light flex-shrink-0 box-border
 * .iqc-condition-preview -> flex flex-col gap-[10px]
 * .iqc-toggle-row        -> flex gap-sp-1
 * .iqc-toggle-pill       -> py-[6px] px-[20px] rounded-[20px] border-[1.5px] border-border-medium bg-deep-navy-5 text-size-3 font-normal text-text-dark
 * .iqc-followup-text     -> text-text-dark
 * .iqc-statement-preview -> flex items-start gap-sp-1 py-[12px] px-[14px] bg-deep-navy-5 border border-border-medium rounded-[6px]
 * .iqc-info-icon         -> text-size-4 text-text-light flex-shrink-0 leading-5
 * .iqc-search-preview    -> flex items-center gap-sp-1 py-[10px] px-[14px] bg-deep-navy-5 border border-border-medium rounded-[6px] text-text-light
 * .iqc-input-preview     -> py-[10px] px-[14px] bg-deep-navy-5 border border-border-medium rounded-[6px]
 * .iqc-hw-preview        -> flex flex-col gap-sp-1
 * .iqc-placeholder-text  -> text-text-light
 *
 * Arbitrary values (no sp-token match): py-[10px], px-[14px], gap-[10px], py-[6px], px-[20px],
 *   rounded-[20px], border-[1.5px], py-[12px], mt-[1px].
 */
import { Badge, Label, SearchIcon } from '@quickmd-llc/design-system'

export type OptionItem = {
  optionId: string
  optionValue: string
  hasFollowUp?: boolean
  followUpText?: string
  optionLocalError: string
  isOptionError: boolean
}

type PatientSeesPreviewProps = {
  rawType: string
  options: OptionItem[]
  questionMess: string
}

function PatientSeesPreview({ rawType, options, questionMess }: PatientSeesPreviewProps) {
  switch (rawType) {
    case 'option':
      return (
        <div className='flex flex-col gap-sp-1'>
          {options.map((opt) => (
            <div
              className='flex items-center gap-[10px] rounded-[6px] border border-border-medium bg-deep-navy-5 px-[14px] py-[10px]'
              key={opt.optionId}>
              <span className='box-border h-4 w-4 flex-shrink-0 rounded-full border-[1.5px] border-text-light' />
              <Label size='md'>{opt.optionValue}</Label>
            </div>
          ))}
        </div>
      )

    case 'condition':
      // No follow-up prompt text: the backend condition-option Input has only IsRequired, so there
      // is no custom text to preview. Show just the answer pills.
      return (
        <div className='flex gap-sp-1'>
          {options.map((opt) => (
            <span
              className='flex items-center gap-sp-1 rounded-[20px] border-[1.5px] border-border-medium bg-deep-navy-5 px-[20px] py-[6px] text-size-3 font-normal text-text-dark'
              key={opt.optionId}>
              <span className='box-border h-4 w-4 flex-shrink-0 rounded-full border-[1.5px] border-text-light' />
              {opt.optionValue}
            </span>
          ))}
        </div>
      )

    case 'statement':
      return (
        <div className='flex items-start gap-sp-1 rounded-[6px] border border-border-medium bg-deep-navy-5 px-[14px] py-[12px]'>
          <span className='flex-shrink-0 text-size-4 leading-5 text-text-light' aria-hidden='true'>
            &#x24D8;
          </span>
          <Label size='md'>{questionMess}</Label>
        </div>
      )

    case 'pharmacyselection':
      return (
        <div className='flex items-center gap-sp-1 rounded-[6px] border border-border-medium bg-deep-navy-5 px-[14px] py-[10px] text-text-light'>
          <SearchIcon iconSize='s' />
          <Label size='md' className='text-text-light'>
            Search for a pharmacy by name, address, or ZIP...
          </Label>
        </div>
      )

    case 'email':
      return (
        <div className='rounded-[6px] border border-border-medium bg-deep-navy-5 px-[14px] py-[10px]'>
          <Label size='md' className='text-text-light'>
            patient@example.com
          </Label>
        </div>
      )

    case 'bodymeasurement':
      return (
        <div className='flex flex-col gap-sp-1'>
          <div className='rounded-[6px] border border-border-medium bg-deep-navy-5 px-[14px] py-[10px]'>
            <Label size='md' className='text-text-light'>
              Height (e.g. 5&apos;10&quot;)
            </Label>
          </div>
          <div className='rounded-[6px] border border-border-medium bg-deep-navy-5 px-[14px] py-[10px]'>
            <Label size='md' className='text-text-light'>
              Weight (lbs)
            </Label>
          </div>
        </div>
      )

    default:
      return (
        <div className='rounded-[6px] border border-border-medium bg-deep-navy-5 px-[14px] py-[10px]'>
          <Label size='md' className='text-text-light'>
            Enter your answer...
          </Label>
        </div>
      )
  }
}

type Props = {
  questionNumber: number
  questionType: string
  rawType: string
  questionMess: string
  isRequired: boolean
  options: OptionItem[]
}

export const IntakeQuestionCard = ({
  questionNumber,
  questionType,
  rawType,
  questionMess,
  isRequired,
  options,
}: Props) => {
  // The stored condition type value ('Single select w/ Follow up') is load-bearing for the edit
  // round-trip, so keep it — just show the friendlier builder label in the badge.
  const displayType = rawType === 'condition' ? 'Yes / No with follow-up' : questionType

  return (
    <div
      className='overflow-hidden rounded-large border border-border-medium bg-white'
      data-testid='pw_if-question-card'>
      <div className='flex items-start gap-sp-1.5 px-sp-3 py-sp-2.5'>
        <Badge variant='active' size='m' className='mt-[1px] flex-shrink-0'>
          Q{questionNumber}
        </Badge>
        <div className='flex min-w-0 flex-1 flex-col gap-sp-1'>
          <Label size='lg' className='break-words text-text-dark'>
            {questionMess}
          </Label>
          <div className='flex flex-wrap gap-sp-1'>
            {displayType ?
              <Badge variant='active' size='s'>
                {displayType}
              </Badge>
            : null}
            {isRequired ?
              <Badge variant='warning' size='s'>
                Required
              </Badge>
            : null}
          </div>
        </div>
      </div>
      <div className='h-px bg-border-medium' />
      <div className='flex flex-col gap-sp-1.5 px-sp-3 py-sp-2'>
        <Label size='sm' className='text-text-light'>
          Patient sees
        </Label>
        <PatientSeesPreview rawType={rawType} options={options} questionMess={questionMess} />
      </div>
    </div>
  )
}
